import React from 'react';

import { Switch, BrowserRouter as Router, Route, Redirect } from "react-router-dom";
import { connect } from "react-redux";

// Import Routes all
import { authRoutes } from "./routes/allRoutes";
import { FALLBACK_ROUTE, PATIENT_ROUTES } from "./constants/routes";

// layouts Format
import VerticalLayout from "./components/VerticalLayout/";
import LayoutWithoutSidebar from "./components/VerticalLayout/layoutWithoutSidebar";
import NonAuthLayout from "./components/NonAuthLayout";

import PrivateRoute from './routes/middleware/privateRoute';
import Dashboard from "./pages/Dashboard/index";
import EmptyPage from './pages/Dashboard/emtyPagePlaceholder';
import PatientManagement from './pages/patientManagement/patientManagement';
import PatientDetails from './pages/patientManagement/patientDetails';
import Call from './pages/Calls/call';
import EditPatientDetails from './pages/patientManagement/editPatientDetails';
import AppointmentDetails from './pages/Dashboard/appointmentDetails';
import Settings from './pages/settings/settings';
import ActivationCode from "./pages/ActivationCode/activationCode";
import Clinicians from "./pages/Clinicians/clinicians";
import LogHistory from "./pages/patientManagement/LogHistory";
import EditChildDetails from "./pages/patientManagement/editChildDetails";
import Voucher from "./pages/Voucher/voucher";
import Statistics from './pages/statistics/statistic';
import StaffActivity from './pages/staffActivity/staffActivity';
import StaffLog from './pages/staffLog/staffLog';

import EmailVerification from './pages/patientVerification/email';
import EmailExpiredLink from './pages/patientVerification/expiredLink';
import ResendEmailLink from './pages/patientVerification/resendEmailLink';
import {ChangePasswordByPatient} from "./pages/patientVerification/ChangePasswordByPatient";
import {ExpiredPatientPasswordLink} from "./pages/patientVerification/ExpiredPatientPasswordLink";
import {ResendPasswordLink} from "./pages/patientVerification/ResendPasswordlink";

import { SUPER_ADMIN } from "./constants/roles";
import { SECTIONS } from "./constants/sections";
import { SectionAccessProvider } from "./components/sectionAccess/SectionAccessContext";


// Import scss
import "./assets/scss/theme.scss";

const App = (props) => {

	function getLayout() {
		let layoutCls = VerticalLayout;

		switch (props.layout.layoutType) {
			default:
				layoutCls = VerticalLayout;
				break;
		}
		return layoutCls;
	}

	const Layout = getLayout();

	const NonAuthmiddleware = ({
		component: Component,
		layout: Layout
	}) => (
		<Route
			render={props => {
				return (
					<Layout>
						<Component {...props} />
					</Layout>
				);
			}}
		/>
	);

	return (
		<React.Fragment>
			<Router>
				{/* Inside the Router: re-checks which sections this admin sees on every navigation. */}
				<SectionAccessProvider>
					<Switch>
						{authRoutes.map((route, idx) => (
							<NonAuthmiddleware
								path={route.path}
								layout={NonAuthLayout}
								component={route.component}
								key={idx}
							/>
						))}

						<PrivateRoute
							exact
							layout={Layout}
							path="/appointments"
							component={Dashboard}
						/>
						<PrivateRoute
							exact
							layout={Layout}
							path="/appointments/appointment"
							component={AppointmentDetails}
						/>
						<PrivateRoute
							exact
							layout={Layout}
							path="/patients-management"
							component={PatientManagement}
						/>
						<PrivateRoute
							exact
							layout={Layout}
							path="/activation-code"
							section={SECTIONS.ACTIVATION_CODES}
							component={ActivationCode}
						/>
						{/* Admins and super admins (Sunil, 24 Oct 2026), never Doctors: the server's visible-sections
						    answer decides (true for an Admin or super admin, false for a Doctor), and the API refuses a
						    Doctor any clinician change. */}
						<PrivateRoute
							exact
							layout={Layout}
							path="/clinicians-management"
							section={SECTIONS.CLINICIANS}
							component={Clinicians}
						/>
						<PrivateRoute
							exact
							layout={Layout}
							path="/user-analytics"
							component={EmptyPage}
						/>
						<PrivateRoute
							exact
							layout={Layout}
							path="/settings"
							component={Settings}
						/>
						{/* Super admins only, always (Sunil, 23 Oct 2026: Admins must never open it): everyone else
						    is sent back to Appointments and the page never mounts. The stored role decides at once
						    (roles), and the visible-sections check brings a demoted super admin's stored role down at
						    the next navigation. Section access never opens it (superAdminOnly), whatever the server
						    answers, and the API refuses everyone else. */}
						<PrivateRoute
							exact
							layout={Layout}
							path="/statistics"
							section={SECTIONS.STATISTICS}
							roles={[SUPER_ADMIN]}
							component={Statistics}
						/>
						{/* Super admins only, always: the section can never be opened to Doctors or Admins. */}
						<PrivateRoute
							exact
							layout={Layout}
							path="/staff-activity"
							section={SECTIONS.STAFF_ACTIVITY}
							component={StaffActivity}
						/>
						{/* Log history (the staff log): super admins only, always - the stored role decides at once (roles),
						    Section access never opens it (superAdminOnly), and the API refuses everyone else too. Not the
						    patient log, which stays at /patients-management/patients/log. */}
						<PrivateRoute
							exact
							layout={Layout}
							path="/log-history"
							section={SECTIONS.STAFF_LOG}
							component={StaffLog}
							roles={[SUPER_ADMIN]}
						/>
						<PrivateRoute
							exact
							layout={LayoutWithoutSidebar}
							path="/call"
							component={Call}
						/>
						<PrivateRoute
							exact
							layout={Layout}
							path="/patients-management/patients"
							component={PatientDetails}
						/>
						<PrivateRoute
							exact
							layout={Layout}
							path="/patients-management/patients/child"
							component={PatientDetails}
						/>
						<PrivateRoute
							exact
							layout={Layout}
							path="/patients-management/patients/log"
							component={LogHistory}
							roles={[SUPER_ADMIN]}
						/>
						<PrivateRoute
							exact
							layout={Layout}
							path="/patients-management/patients/edit"
							component={EditPatientDetails}
						/>
						<PrivateRoute
							exact
							layout={Layout}
							path="/patients-management/patients/child/edit"
							component={EditChildDetails}
						/>
						<PrivateRoute
							exact
							layout={Layout}
							path="/voucher"
							section={SECTIONS.VOUCHERS}
							component={Voucher}
						/>
						<Route
							exact
							layout={Layout}
							path="/email-verification"
							component={EmailVerification}
						/>
						<Route
							exact
							layout={Layout}
							path="/email-expired-link"
							component={EmailExpiredLink}
						/>
						<Route
							exact
							layout={Layout}
							path="/resend-email-link"
							component={ResendEmailLink}
						/>
						<Route
							exact
							layout={Layout}
							path={PATIENT_ROUTES.restorePassword}
							component={ChangePasswordByPatient}
						/>
						<Route
							exact
							layout={Layout}
							path={PATIENT_ROUTES.passwordExpiredLink}
							component={ExpiredPatientPasswordLink }
						/>
						<Route
							exact
							layout={Layout}
							path={PATIENT_ROUTES.resendPasswordLink}
							component={ResendPasswordLink}
						/>
						<Redirect from="*" to={FALLBACK_ROUTE} />
					</Switch>
				</SectionAccessProvider>
			</Router>
		</React.Fragment>
	);
}


const mapStateToProps = state => {
	return {
		layout: state.Layout
	};
};

export default connect(mapStateToProps)(App);
